import React, { useEffect, useMemo, useState } from "react";
import { useCollection } from "@amzn/awsui-collection-hooks";
import {
  Button,
  Divider,
  Header,
  Pagination,
  Table,
  Box,
  PropertyFilter,
  PropertyFilterProps,
  SpaceBetween,
} from "@amzn/awsui-components-console";
import {
  FixedFeeConfig,
  FixedFeeType,
  FuseAccountingCentralDatastore,
  GetFixedFeeConfigCommand,
} from "@amzn/fuse-accounting-central-datastore-client";
import {
  FIXED_FEE_CONFIG_ATTRIBUTE_CONFIGS,
  FIXED_FEE_CONFIG_ATTRIBUTES,
  FIXED_FEE_CONFIG_FILTERS,
  getAllFixedFeeConfigColumns,
  getFixedFeeConfigTableColDefs,
} from "src/components/allFixedFeeConfigsPage/fixed-fee-config-attributes";
import {
  evaluateQueryOnItem,
  getPropertyFilterOptions,
  MIN_FREE_TEXT_LENGTH,
} from "src/components/allFixedFeeConfigsPage/fixed-fee-config-filtering.helper";
import AdditionalInfoPopover, {
  AdditionalInfoModalType,
} from "src/components/allFixedFeeConfigsPage/components/additional-info-popover";
import { RegionAwareConfig } from "src/config/facd-all-regions";
import { RegionalDomain } from "src/config/BartLambdaConfig";
import { PartnerPortalLink } from "../fixedFeeConfigPage/partner-portal-link";
import { CustomizeModal } from "src/components/allFixedFeeConfigsPage/components/customize-modal";
import { useColumnProfilesStore } from "src/stores/useColumnProfilesStore";

// Pagination labels
const fixedFeeConfigPaginationLabels = {
  nextPageLabel: "Next page",
  pageLabel: (pageNumber: number) => `Go to page ${pageNumber}`,
  previousPageLabel: "Previous page",
};

// Function to get matches count text
function getMatchesCountText(count: number) {
  return count === 1 ? "1 match" : `${count} matches`;
}

// Empty state component
function EmptyState({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle: string;
  action: React.ReactNode;
}) {
  return (
    <Box textAlign="center" color="inherit">
      <Box variant="strong" textAlign="center" color="inherit">
        {title}
      </Box>
      <Box variant="p" padding={{ bottom: "s" }} color="inherit">
        {subtitle}
      </Box>
      {action}
    </Box>
  );
}

interface FixedFeeConfigTableProps {
  searchResults: RegionAwareConfig[];
  clients?: Record<string, FuseAccountingCentralDatastore>;
  onRefresh?: () => void;
}

const FixedFeeConfigTableDisplay: React.FC<FixedFeeConfigTableProps> = ({
  searchResults,
  clients,
  onRefresh,
}) => {
  const [allItems, setAllItems] = useState<RegionAwareConfig[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingConfig, setLoadingConfig] = useState<boolean>(false);
  const [configs, setConfigs] = useState<{ [key: string]: FixedFeeConfig }>({});
  const [selectedItems, setSelectedItems] = useState<RegionAwareConfig[]>([]);
  const [customizeModalVisible, setCustomizeModalVisible] =
    useState<boolean>(false);

  // Visible columns and concise view are driven by the active column profile
  // (persisted locally).
  const { activeProfile, customProfiles, customConcise } =
    useColumnProfilesStore();
  const allColumnIds = useMemo(
    () => getAllFixedFeeConfigColumns().map((c) => c.id),
    [],
  );
  const visibleColumnIds = useMemo(() => {
    if (activeProfile === "default") return allColumnIds;
    const stored = customProfiles[activeProfile];
    return stored
      ? allColumnIds.filter((id) => stored.includes(id))
      : allColumnIds;
  }, [activeProfile, customProfiles, allColumnIds]);
  const conciseView =
    activeProfile === "default" ? false : customConcise[activeProfile];

  // By default, PRODUCTION ENABLED FixedFeeConfigs which are "currently" ongoing
  const [query, setQuery] = useState<PropertyFilterProps.Query>({
    tokens: [
      { propertyKey: "environment", operator: "=", value: "PRODUCTION" },
      { propertyKey: "dealStatus", operator: "=", value: "ENABLED" },
      { propertyKey: "dealDateStatus", operator: "=", value: "CURRENT" },
    ],
    operation: "and",
  });

  // Default preferences for the table
  const FIXED_FEE_CONFIG_DEFAULT_PREFERENCES = {
    pageSize: 15,
    visibleContent: FIXED_FEE_CONFIG_ATTRIBUTES.filter(
      (attribute) => !FIXED_FEE_CONFIG_ATTRIBUTE_CONFIGS[attribute].hidden,
    ),
    wrapLines: true,
    custom: "table",
  };

  useEffect(() => {
    // Focus is now handled by the Input component
  }, []);

  useEffect(() => {
    if (Array.isArray(searchResults)) {
      setAllItems(searchResults);
      setLoading(false);
    } else {
      setAllItems([]);
      setLoading(searchResults === "init");
    }
  }, [searchResults]);

  const getFixedFeeConfig = async (fixedFeeId: string, region?: string) => {
    if (!clients || !region) return;
    setLoadingConfig(true);
    try {
      const response = await clients[region as RegionalDomain].send(
        new GetFixedFeeConfigCommand({ fixedFeeId }),
      );
      setConfigs((prev) => ({
        ...prev,
        [fixedFeeId]: response.fixedFeeConfig!,
      }));
    } catch (error) {
      console.error("An error occurred in getFixedFeeConfig:", error);
    } finally {
      setLoadingConfig(false);
    }
  };

  const clearFilter = () => {
    setQuery({ tokens: [], operation: "and" });
    actions.setFiltering("");
  };

  const {
    items,
    actions,
    filteredItemsCount,
    collectionProps,
    filterProps,
    paginationProps,
  } = useCollection(allItems, {
    filtering: {
      empty: (
        <EmptyState
          title="No FixedFee Configs"
          subtitle="No FixedFee Configs to display."
          action={null}
        />
      ),
      noMatch: (
        <EmptyState
          title="No matches"
          subtitle="We can't find a match."
          action={<Button onClick={clearFilter}>Clear all filters</Button>}
        />
      ),
      filteringFunction: (item: RegionAwareConfig) =>
        evaluateQueryOnItem(query, item),
    },
    pagination: {
      pageSize: FIXED_FEE_CONFIG_DEFAULT_PREFERENCES.pageSize,
    },
    sorting: {},
    selection: {},
  });

  return (
    <>
      <Box padding={{ left: "l", right: "l" }}>
        <Table
          {...collectionProps}
          selectionType="single"
          selectedItems={selectedItems}
          onSelectionChange={({ detail }) =>
            setSelectedItems(detail.selectedItems)
          }
          visibleColumns={visibleColumnIds}
          wrapLines={FIXED_FEE_CONFIG_DEFAULT_PREFERENCES.wrapLines}
          loading={loading}
          loadingText="Loading FixedFeeConfigs"
          stickyHeaderVerticalOffset={1}
          stickyColumns={{ first: 1, last: 0 }}
          variant="full-page"
          header={
            <Header
              variant="awsui-h1-sticky"
              actions={
                <SpaceBetween direction="horizontal" size="xs">
                  <Button
                    variant="normal"
                    iconName="refresh"
                    onClick={() => {
                      setSelectedItems([]);
                      onRefresh?.();
                    }}
                    disabled={loading}
                  />
                  {[
                    AdditionalInfoModalType.Issuance,
                    AdditionalInfoModalType.AdjustmentSubBalanceShift,
                    AdditionalInfoModalType.Matcher,
                  ].map((modalType) => {
                    const isTac =
                      selectedItems[0]?.fixedFeeType ===
                      FixedFeeType.TERMINATION_AT_CONVENIENCE;
                    const isTacDisabled =
                      isTac &&
                      modalType ===
                        AdditionalInfoModalType.AdjustmentSubBalanceShift;
                    const isDisabled =
                      selectedItems.length === 0 || isTacDisabled;

                    const button = (
                      <Button
                        iconName="file-open"
                        disabled={isDisabled}
                        onClick={() => {
                          if (
                            selectedItems[0] &&
                            !(selectedItems[0].fixedFeeId! in configs)
                          ) {
                            getFixedFeeConfig(
                              selectedItems[0].fixedFeeId!,
                              selectedItems[0].region,
                            );
                          }
                        }}
                        variant="normal"
                      >
                        {modalType === AdditionalInfoModalType.Issuance
                          ? "View Issuance"
                          : modalType ===
                              AdditionalInfoModalType.AdjustmentSubBalanceShift
                            ? "View Adjustment/SubBalanceShift"
                            : "View Matcher"}
                      </Button>
                    );

                    if (isTacDisabled) {
                      return (
                        <span
                          key={modalType}
                          title="Not supported for config with Termination at Convenience"
                        >
                          {button}
                        </span>
                      );
                    }

                    return (
                      <AdditionalInfoPopover
                        key={modalType}
                        fixedFeeConfig={
                          selectedItems[0]
                            ? configs[selectedItems[0].fixedFeeId!]
                            : undefined
                        }
                        loading={loadingConfig}
                        child={button}
                        domain={
                          (selectedItems[0]?.region as RegionalDomain) ??
                          "devo-FE"
                        }
                        modalType={modalType}
                      />
                    );
                  })}
                  <PartnerPortalLink fixedFeeConfig={selectedItems[0]} />
                  <Divider orientation="vertical" />
                  <Button
                    variant="normal"
                    iconName="settings"
                    onClick={() => setCustomizeModalVisible(true)}
                  >
                    Customize
                  </Button>
                </SpaceBetween>
              }
              counter={`(${allItems.length})`}
            >
              FixedFee Configs
            </Header>
          }
          columnDefinitions={getFixedFeeConfigTableColDefs(conciseView)}
          items={items}
          pagination={
            <Pagination
              {...paginationProps}
              ariaLabels={fixedFeeConfigPaginationLabels}
            />
          }
          filter={
            <PropertyFilter
              {...filterProps}
              query={query}
              onChange={({ detail }) => setQuery(detail)}
              filteringProperties={FIXED_FEE_CONFIG_FILTERS}
              filteringOptions={FIXED_FEE_CONFIG_FILTERS.reduce<
                PropertyFilterProps.FilteringOption[]
              >(
                (acc, property) =>
                  acc.concat(getPropertyFilterOptions(property.key, allItems)),
                [],
              )}
              countText={getMatchesCountText(filteredItemsCount ?? 0)}
              expandToViewport={true}
              filteringAriaLabel="Find FixedFee Configs"
              filteringPlaceholder="Find FixedFee Configs"
              // Free text matches a case-insensitive substring of any column
              // (including offer contract IDs, which no column displays).
              // Only "contains" is offered; see MIN_FREE_TEXT_LENGTH for why
              // short queries match nothing.
              freeTextFiltering={{
                operators: [":"],
                defaultOperator: ":",
              }}
              i18nStrings={{
                enteredTextLabel: (value) =>
                  value.length < MIN_FREE_TEXT_LENGTH
                    ? `Contains: ${value} \u2014 enter at least ${MIN_FREE_TEXT_LENGTH} characters`
                    : `Contains: ${value}`,
                groupPropertiesText: "Properties",
                groupValuesText: "Values",
                propertyText: "Property",
                valueText: "Value",
                operatorText: "Operator",
                operatorsText: "Operators",
                allPropertiesLabel: "All properties",
                operationAndText: "and",
                operationOrText: "or",
                cancelActionText: "Cancel",
                clearFiltersText: "Clear filters",
                operatorContainsText: "Contains",
                operatorDoesNotContainText: "Does not contain",
                operatorEqualsText: "Equals",
                operatorDoesNotEqualText: "Does not equal",
                applyActionText: "Apply",
              }}
            />
          }
        />
      </Box>
      <CustomizeModal
        visible={customizeModalVisible}
        onDismiss={() => setCustomizeModalVisible(false)}
      />
    </>
  );
};

export default FixedFeeConfigTableDisplay;
